import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";
import { describeError } from "@/components/operations/wire";
import type { OperationsTechnicianPosition } from "@/type/api";

/** The SSE resource the server publishes after storing a valid Traccar fix
 * (platform/event.go's `TechnicianPositionResource`). The event carries no
 * payload, so receipt means one thing: the positions are worth re-reading. */
export const TECHNICIAN_POSITION_RESOURCE = "sync:technician-position";

/**
 * The Operations map's technician overlay: one row per district member whose
 * device has reported a valid fix, which the map draws as an avatar at the
 * position of the newest one.
 *
 * Unlike the rest of the app's sync stores, this one is not subscribed at module
 * load. Positions are the Operations page's, and a technician update is a
 * frequent event (every accepted fix), so a stream listener that outlived the
 * page would re-read the roster on every fix for a surface nobody is looking at.
 * The page therefore starts the store when it mounts and stops it when it
 * unmounts, and that lifecycle is the only reason this store is a setup store
 * with handles rather than the fire-and-forget subscription its siblings use.
 */
export const useTechnicianPositionStore = defineStore(
	"technician-position",
	() => {
		// State
		const positions = ref<OperationsTechnicianPosition[]>([]);
		const loading = ref(false);
		const error = ref<string | null>(null);

		// The two SSE handles live as long as a surface is showing the overlay.
		// Null means "not listening", which is also what makes `start` idempotent.
		let resourceSub: string | null = null;
		let reconnectSub: string | null = null;

		/**
		 * The overlay's whole read: one row per technician with a valid fix. A
		 * failed read keeps the rows already drawn — the pins still show the last
		 * positions the office knew, and `error` says why they may be old — because
		 * a refresh that blanks the map is worse than one that changes nothing.
		 *
		 * The promise never rejects: the only callers are the SSE handlers and the
		 * page's mount, and an unhandled rejection from an event nobody awaited
		 * would reach the console rather than the operator.
		 */
		async function fetch(): Promise<void> {
			loading.value = true;
			error.value = null;
			try {
				const rows = await apiClient.JSONGet<OperationsTechnicianPosition[]>(
					"/api/operations/technician-positions",
				);
				positions.value = rows ?? [];
			} catch (e) {
				error.value = describeError(e);
			} finally {
				loading.value = false;
			}
		}

		/**
		 * Subscribe while a surface is showing the overlay, and fetch once so the
		 * map has pins before the first event arrives.
		 *
		 * A reconnect is a gap as much as a failure: events published while the
		 * stream was down were never delivered, so the positions are re-read rather
		 * than trusted to be current.
		 */
		function start(): void {
			if (resourceSub === null) {
				resourceSub = SSEManager.subscribe((msg: SSEMessageResource) => {
					if (msg.resource === TECHNICIAN_POSITION_RESOURCE) void fetch();
				});
				reconnectSub = SSEManager.subscribeReconnect(() => void fetch());
			}
			void fetch();
		}

		/**
		 * Stop listening when the page that showed the overlay goes away. The rows
		 * are kept rather than cleared: this store outlives the view, and coming
		 * back to the page re-reads them in the same tick it mounts, so there is
		 * nothing a blank slate would protect the operator from.
		 */
		function stop(): void {
			if (resourceSub !== null) {
				SSEManager.unsubscribe(resourceSub);
				resourceSub = null;
			}
			if (reconnectSub !== null) {
				SSEManager.unsubscribe(reconnectSub);
				reconnectSub = null;
			}
		}

		return { positions, loading, error, fetch, start, stop };
	},
);
